// Version: 1.4
// v1.4: Phone width: the Monitored Services table stacks one service per block (index.css v1.6), so Remove is no longer cut off.
// v1.3: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState } from 'react';
import { authFetch } from './auth';

const DEFAULT_SERVICES = [
  { name: 'Action1Agent', display: 'Action1',          required: true  },
  { name: 'DWAgent',      display: 'DWService',        required: true  },
  { name: 'WinDefend',    display: 'Windows Defender', required: true  },
];

export default function PackEditor() {
  const [pack, setPack]           = useState(null);
  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState(null);
  const [newName, setNewName]     = useState('');
  const [newDisplay, setNewDisplay] = useState('');
  const [newRequired, setNewRequired] = useState(true);

  const flash = (text, err = false) => {
    setMsg({ text, err });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = () => {
    setLoading(true);
    authFetch('/api/endpoint/pack')
      .then(r => r.json())
      .then(d => {
        setPack(d);
        setServices(d.monitored_services || DEFAULT_SERVICES);
      })
      .catch(() => flash('Failed to load pack', true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = { ...pack, monitored_services: services };
      const r = await authFetch('/api/admin/endpoint/pack', {
        method: 'PUT',
        body: JSON.stringify(updated),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || 'Save failed'); }
      flash('Pack saved successfully');
      load();
    } catch (e) { flash(e.message, true); }
    setSaving(false);
  };

  const handleAdd = () => {
    const name = newName.trim();
    const display = newDisplay.trim();
    if (!name || !display) return;
    if (services.find(s => s.name.toLowerCase() === name.toLowerCase())) {
      flash('A service with that name already exists', true);
      return;
    }
    setServices(prev => [...prev, { name, display, required: newRequired }]);
    setNewName('');
    setNewDisplay('');
    setNewRequired(true);
  };

  const handleRemove = (name) => {
    setServices(prev => prev.filter(s => s.name !== name));
  };

  const handleToggleRequired = (name) => {
    setServices(prev => prev.map(s => s.name === name ? { ...s, required: !s.required } : s));
  };

  if (loading) return <div style={{padding:32,color:'var(--text-muted)',fontSize:13}}>Loading pack...</div>;

  return (
    <div style={{maxWidth:860,margin:'0 auto',padding:'24px 16px'}}>
      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:22,fontWeight:700,margin:0}}>Pack Editor</h1>
        <div style={{fontSize:13,color:'var(--text-muted)',marginTop:4}}>
          {pack?._meta?.name} · v{pack?._meta?.version} · {pack?._meta?.platform}
        </div>
      </div>

      {msg && (
        <div style={{marginBottom:16,padding:'10px 14px',borderRadius:6,fontSize:13,
          background: msg.err ? 'rgba(255,45,85,0.08)' : 'rgba(48,209,88,0.08)',
          border: `1px solid ${msg.err ? 'rgba(255,45,85,0.3)' : 'rgba(48,209,88,0.3)'}`,
          color: msg.err ? '#ff2d55' : '#30d158'}}>
          {msg.text}
        </div>
      )}

      {/* Monitored Services */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15,marginBottom:4}}>Monitored Services</div>
        <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:16}}>
          The agent checks these Windows services on every heartbeat and reports their status. Required services trigger a Teams alert when stopped or not installed.
        </div>

        {/* Service list */}
        <table className="stack-on-mobile" style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--border)'}}>
              <th style={{textAlign:'left',padding:'6px 8px',fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Service Name</th>
              <th style={{textAlign:'left',padding:'6px 8px',fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Display Label</th>
              <th style={{textAlign:'center',padding:'6px 8px',fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Required</th>
              <th style={{textAlign:'right',padding:'6px 8px',fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}></th>
            </tr>
          </thead>
          <tbody>
            {services.map(svc => (
              <tr key={svc.name} style={{borderBottom:'1px solid var(--border)'}}>
                <td className="wide" style={{padding:'8px',fontFamily:'monospace',fontSize:12,color:'var(--accent)'}}>{svc.name}</td>
                <td className="wide" style={{padding:'8px',fontSize:13}}>{svc.display}</td>
                <td style={{padding:'8px',textAlign:'center'}}>
                  <button className="btn btn-secondary btn-sm" aria-pressed={!!svc.required} onClick={() => handleToggleRequired(svc.name)}>
                    {svc.required ? 'Yes' : 'No'}
                  </button>
                </td>
                <td style={{padding:'8px',textAlign:'right'}}>
                  <button className="btn btn-danger btn-sm"
                    onClick={() => handleRemove(svc.name)}
                   >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr><td colSpan={4} style={{padding:'16px 8px',textAlign:'center',color:'var(--text-muted)',fontSize:12}}>No services configured</td></tr>
            )}
          </tbody>
        </table>

        {/* Add new service */}
        <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Add Service</div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <input className="input-sm"
              type="text"
              placeholder="Service name (e.g. WinDefend)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              style={{ flex:1, minWidth:160 }}
            />
            <input className="input-sm"
              type="text"
              placeholder="Display label (e.g. Windows Defender)"
              value={newDisplay}
              onChange={e => setNewDisplay(e.target.value)}
              style={{ flex:1, minWidth:180 }}
            />
            <button className="btn btn-secondary btn-sm" aria-pressed={newRequired} onClick={() => setNewRequired(r => !r)} style={{flexShrink:0}}>
              {newRequired ? 'Required' : 'Optional'}
            </button>
            <button className="btn btn-primary" style={{ flexShrink: 0, opacity: !newName.trim()||!newDisplay.trim() ? 0.5 : 1 }}
              onClick={handleAdd}
              disabled={!newName.trim() || !newDisplay.trim()}
             
             >
              + Add
            </button>
          </div>
        </div>
      </div>

      {/* Pack meta info */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>Pack Information</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12}}>
          {[
            ['Name',        pack?._meta?.name],
            ['Version',     pack?._meta?.version],
            ['Platform',    pack?._meta?.platform],
            ['Interval',    pack?._meta?.interval_secs ? `${pack._meta.interval_secs}s` : '—'],
            ['Queries',     Object.keys(pack?.queries || {}).length],
            ['Services',    services.length],
          ].map(([label, value]) => (
            <div key={label} style={{padding:'10px 12px',borderRadius:6,border:'1px solid var(--border)',background:'rgba(255,255,255,0.02)'}}>
              <div style={{fontSize:10,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>{label}</div>
              <div style={{fontSize:14,fontWeight:600}}>{value ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Save button */}
      <div style={{display:'flex',justifyContent:'flex-end'}}>
        <button className="btn btn-primary" style={{ opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}
         >
          {saving ? <span className="spinner"/> : 'Save Pack'}
        </button>
      </div>
    </div>
  );
}